// Every figure the terminal shows goes through here. A missing value renders as
// "unavailable" in tables and "—" in tiles, never as zero.

const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const integer = new Intl.NumberFormat("en-US");
const decimal2 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** Compact units, smallest first, with the decimals each shows. */
const COMPACT_UNITS = [
  { size: 1_000, suffix: "K", digits: 1 },
  { size: 1_000_000, suffix: "M", digits: 1 },
  { size: 1_000_000_000, suffix: "B", digits: 2 },
] as const;

export function compactUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const abs = Math.abs(value);
  if (Math.round(abs) < 1_000) return usd0.format(value);
  // The unit is chosen from the figure as it will print, so a value that
  // rounds up to 1000 of one unit moves to the next: 999,950 is $1.0M, not
  // $1000.0K, and 999,950,000 is $1.00B, not $1000.0M.
  const sign = value < 0 ? "-" : "";
  for (const [i, unit] of COMPACT_UNITS.entries()) {
    const text = (abs / unit.size).toFixed(unit.digits);
    if (Number(text) < 1_000 || i === COMPACT_UNITS.length - 1) return `${sign}$${text}${unit.suffix}`;
  }
  return usd0.format(value);
}

export const price = (value: number) => usd2.format(value);
/** Full-precision dollars without cents, for exact readouts: $17,291,444. */
export const wholeUsd = (value: number) => usd0.format(value);
export const count = (value: number) => integer.format(value);
export const amount = (value: number) => decimal2.format(value);

export function percent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

export function signedPercent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return "—";
  const text = `${(value * 100).toFixed(digits)}%`;
  return value > 0 ? `+${text}` : text;
}

/** Strips the issuer suffix Robinhood appends to every token name. */
export const tokenName = (name: string | null) => (name ?? "").replace(/\s*•\s*Robinhood Token\s*$/u, "");

export function utcTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
}

export function ago(iso: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}
